import React, { useEffect, useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  Modal,
  RefreshControl,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { COLORS, FONTS } from '../../constants/config';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { PageContainer, Breadcrumb } from '../../components';
import { auditLogsApi, AuditLogEntry } from '../../services/apiService';

/**
 * سجل التدقيق (Audit Log) — DTS chapter 9.
 *
 * Reads from `adaa_audit.AuditLog` via /api/audit/logs/ (already populated
 * by the request middleware — no instrumentation needed in CPMS code).
 * Read-only: filtering, search, per-row details modal. Per the DTS
 * permissions matrix: Strategy Team + DG + Director + Tech Admin can view.
 */

const ACTION_LABELS: Record<string, string> = {
  CREATE: 'إنشاء',
  UPDATE: 'تعديل',
  DELETE: 'حذف',
  LOGIN: 'تسجيل دخول',
  LOGOUT: 'تسجيل خروج',
  LOGIN_FAILED: 'فشل تسجيل دخول',
  RATE_LIMITED: 'تجاوز معدل الطلبات',
  AUTH_EVENT: 'حدث مصادقة',
};

const ACTION_COLORS: Record<string, { bg: string; fg: string }> = {
  CREATE:        { bg: '#DCFCE7', fg: '#15803D' },
  UPDATE:        { bg: '#EFF6FF', fg: '#1E40AF' },
  DELETE:        { bg: '#FEE2E2', fg: '#B91C1C' },
  LOGIN:         { bg: '#F0FDFA', fg: '#0F766E' },
  LOGOUT:        { bg: '#F3F4F6', fg: '#374151' },
  LOGIN_FAILED:  { bg: '#FEE2E2', fg: '#B91C1C' },
  RATE_LIMITED:  { bg: '#FEF3C7', fg: '#92400E' },
  AUTH_EVENT:    { bg: '#FFFBEB', fg: '#92400E' },
};

const PAGE_SIZE = 20;

export default function AuditLogScreen() {
  const { showError } = useNotification();
  const { colors, isDark } = useTheme();
  const { user } = useAuth();

  // Per DTS chapter 9 permissions matrix.
  const canView = !!user?.is_superuser
    || (user?.permissions || []).includes('hrcpms.audit.view')
    || (user?.permissions || []).includes('hrcpms.strategic_goals.create')
    || (user?.permissions || []).includes('hrcpms.review.approve_reject');

  const [items, setItems] = useState<AuditLogEntry[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Detail modal
  const [selected, setSelected] = useState<AuditLogEntry | null>(null);

  const fetchData = useCallback(async () => {
    if (!canView) return;
    try {
      const params: Record<string, any> = {
        page,
        page_size: PAGE_SIZE,
      };
      if (search) params.search = search;
      if (actionFilter) params.action = actionFilter;
      if (entityTypeFilter) params.entity_type = entityTypeFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const resp = await auditLogsApi.list(params);
      setItems(resp.results || []);
      setCount(resp.count || 0);
    } catch (err: any) {
      console.error('Failed to load audit logs:', err);
      const msg = err?.response?.status === 403
        ? 'ليست لديك صلاحية الاطّلاع على سجل التدقيق'
        : 'تعذّر تحميل سجل التدقيق';
      showError(msg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [canView, page, search, actionFilter, entityTypeFilter, dateFrom, dateTo, showError]);

  useFocusEffect(useCallback(() => { fetchData(); }, [fetchData]));

  useEffect(() => { setPage(1); }, [search, actionFilter, entityTypeFilter, dateFrom, dateTo]);

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  const formatTime = (iso: string) => {
    try {
      const d = new Date(iso);
      return `${d.toLocaleDateString('ar')} ${d.toLocaleTimeString('ar', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
    } catch {
      return iso;
    }
  };

  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textMuted: colors.textMuted,
    inputBackground: colors.inputBackground,
    border: colors.border,
  };

  if (!canView) {
    return (
      <PageContainer>
        <View style={{ padding: 40, alignItems: 'center' }}>
          <Text style={{ fontSize: 16, color: themeColors.textMuted, fontFamily: FONTS.medium }}>
            ليست لديك صلاحية الاطّلاع على سجل التدقيق
          </Text>
        </View>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ScrollView
        contentContainerStyle={{ padding: 16, gap: 16 }}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); fetchData(); }} />
        }
      >
        <Breadcrumb items={[
          { label: 'الرئيسية', icon: 'home' as any },
          { label: 'سجل التدقيق' },
        ]} />

        {/* Header */}
        <View style={[styles.headerCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
          <Text style={[styles.title, { color: themeColors.textPrimary }]}>سجل التدقيق</Text>
          <Text style={[styles.subtitle, { color: themeColors.textMuted }]}>
            تتبّع كل العمليات على النظام: تسجيل الدخول، الإنشاء، التعديل، الحذف، الاعتمادات.
            البيانات للعرض فقط ولا يمكن تعديلها أو حذفها (DTS الفصل 9).
          </Text>
          <Text style={[styles.subtitle, { color: themeColors.textMuted, marginTop: 4 }]}>
            إجمالي السجلات: <Text style={{ fontFamily: FONTS.medium }}>{count}</Text>
          </Text>
        </View>

        {/* Filters */}
        <View style={[styles.filterCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
          <Text style={[styles.filterTitle, { color: themeColors.textPrimary }]}>الفلاتر</Text>

          <View style={styles.filterRow}>
            <View style={styles.filterField}>
              <Text style={[styles.filterLabel, { color: themeColors.textMuted }]}>بحث</Text>
              <TextInput
                value={search}
                onChangeText={setSearch}
                placeholder="ابحث في الرسالة، المستخدم، الكيان..."
                placeholderTextColor={themeColors.textMuted}
                style={[styles.input, { backgroundColor: themeColors.inputBackground, borderColor: themeColors.border, color: themeColors.textPrimary }]}
                textAlign="right"
              />
            </View>

            <View style={styles.filterField}>
              <Text style={[styles.filterLabel, { color: themeColors.textMuted }]}>نوع العملية</Text>
              <View style={[styles.input, { backgroundColor: themeColors.inputBackground, borderColor: themeColors.border, padding: 0 }]}>
                <select
                  value={actionFilter}
                  onChange={(e) => setActionFilter(e.target.value)}
                  style={{
                    width: '100%', height: 44, padding: '0 12px',
                    background: 'transparent', border: 'none', outline: 'none',
                    color: themeColors.textPrimary as string,
                    fontFamily: FONTS.regular as any, fontSize: 14,
                    textAlign: 'right', direction: 'rtl',
                  }}
                >
                  <option value="">الكل</option>
                  {Object.entries(ACTION_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </View>
            </View>
          </View>

          <View style={styles.filterRow}>
            <View style={styles.filterField}>
              <Text style={[styles.filterLabel, { color: themeColors.textMuted }]}>نوع الكيان</Text>
              <TextInput
                value={entityTypeFilter}
                onChangeText={setEntityTypeFilter}
                placeholder="مثل: KPI, Initiative, Auth"
                placeholderTextColor={themeColors.textMuted}
                style={[styles.input, { backgroundColor: themeColors.inputBackground, borderColor: themeColors.border, color: themeColors.textPrimary }]}
                textAlign="right"
              />
            </View>

            <View style={styles.filterField}>
              <Text style={[styles.filterLabel, { color: themeColors.textMuted }]}>من تاريخ</Text>
              <TextInput
                value={dateFrom}
                onChangeText={setDateFrom}
                // @ts-ignore — web prop
                type="date"
                style={[styles.input, { backgroundColor: themeColors.inputBackground, borderColor: themeColors.border, color: themeColors.textPrimary }]}
              />
            </View>

            <View style={styles.filterField}>
              <Text style={[styles.filterLabel, { color: themeColors.textMuted }]}>إلى تاريخ</Text>
              <TextInput
                value={dateTo}
                onChangeText={setDateTo}
                // @ts-ignore — web prop
                type="date"
                style={[styles.input, { backgroundColor: themeColors.inputBackground, borderColor: themeColors.border, color: themeColors.textPrimary }]}
              />
            </View>
          </View>

          <TouchableOpacity
            style={styles.clearBtn}
            onPress={() => {
              setSearch(''); setActionFilter(''); setEntityTypeFilter('');
              setDateFrom(''); setDateTo('');
            }}
          >
            <Text style={styles.clearBtnText}>مسح الفلاتر</Text>
          </TouchableOpacity>
        </View>

        {/* Table */}
        {loading ? (
          <View style={{ padding: 40, alignItems: 'center' }}>
            <ActivityIndicator size="large" color={COLORS.primary} />
          </View>
        ) : items.length === 0 ? (
          <View style={[styles.emptyCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
            <Text style={[styles.emptyText, { color: themeColors.textMuted }]}>
              لا توجد سجلات مطابقة للفلاتر
            </Text>
          </View>
        ) : (
          <View style={[styles.tableCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
            {/* Header row */}
            <View style={[styles.tableRow, styles.tableHeader, { borderBottomColor: themeColors.cardBorder }]}>
              <Text style={[styles.cellTime, styles.headerCellText]}>الوقت</Text>
              <Text style={[styles.cellUser, styles.headerCellText]}>المستخدم</Text>
              <Text style={[styles.cellAction, styles.headerCellText]}>العملية</Text>
              <Text style={[styles.cellEntity, styles.headerCellText]}>الكيان</Text>
              <Text style={[styles.cellPath, styles.headerCellText]}>المسار</Text>
              <Text style={[styles.cellStatus, styles.headerCellText]}>الحالة</Text>
            </View>

            {items.map((row) => {
              const actionStyle = ACTION_COLORS[row.action] || { bg: '#F3F4F6', fg: '#374151' };
              return (
                <TouchableOpacity
                  key={row.id}
                  style={[styles.tableRow, { borderBottomColor: themeColors.cardBorder }]}
                  onPress={() => setSelected(row)}
                >
                  <Text style={[styles.cellTime, { color: themeColors.textMuted }]} numberOfLines={1}>
                    {formatTime(row.timestamp)}
                  </Text>
                  <Text style={[styles.cellUser, { color: themeColors.textPrimary }]} numberOfLines={1}>
                    {row.user_display || (row.user_id ? `User ${row.user_id}` : '—')}
                  </Text>
                  <View style={styles.cellAction}>
                    <View style={[styles.actionBadge, { backgroundColor: actionStyle.bg }]}>
                      <Text style={[styles.actionBadgeText, { color: actionStyle.fg }]}>
                        {ACTION_LABELS[row.action] || row.action}
                      </Text>
                    </View>
                  </View>
                  <Text style={[styles.cellEntity, { color: themeColors.textPrimary }]} numberOfLines={1}>
                    {row.entity_type}{row.entity_id ? ` #${row.entity_id}` : ''}
                  </Text>
                  <Text style={[styles.cellPath, { color: themeColors.textMuted }]} numberOfLines={1}>
                    {row.request_method ? `${row.request_method} ` : ''}{row.request_path || '—'}
                  </Text>
                  <Text style={[
                    styles.cellStatus,
                    { color: row.status_code && row.status_code >= 400 ? '#B91C1C' : themeColors.textMuted },
                  ]}>
                    {row.status_code ?? '—'}
                  </Text>
                </TouchableOpacity>
              );
            })}

            {/* Pagination */}
            <View style={[styles.pagination, { borderTopColor: themeColors.cardBorder }]}>
              <Text style={[styles.paginationText, { color: themeColors.textMuted }]}>
                صفحة {page} من {totalPages} — {count} سجل
              </Text>
              <View style={{ flexDirection: 'row-reverse', gap: 8 }}>
                <TouchableOpacity
                  style={[styles.pageBtn, page <= 1 && { opacity: 0.4 }]}
                  disabled={page <= 1}
                  onPress={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <Text style={styles.pageBtnText}>السابق</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.pageBtn, page >= totalPages && { opacity: 0.4 }]}
                  disabled={page >= totalPages}
                  onPress={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  <Text style={styles.pageBtnText}>التالي</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        )}
      </ScrollView>

      {/* Detail modal */}
      <Modal visible={!!selected} transparent animationType="fade" onRequestClose={() => setSelected(null)}>
        {selected && (
          <View style={styles.modalOverlay}>
            <View style={[styles.modalCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
              <ScrollView contentContainerStyle={{ paddingBottom: 8 }}>
                <Text style={[styles.modalTitle, { color: themeColors.textPrimary }]}>
                  تفاصيل سجل التدقيق #{selected.id}
                </Text>

                <DetailRow label="الوقت" value={formatTime(selected.timestamp)} colors={themeColors} />
                <DetailRow label="المستخدم" value={`${selected.user_display} (id=${selected.user_id ?? '—'})`} colors={themeColors} />
                <DetailRow label="العملية" value={ACTION_LABELS[selected.action] || selected.action} colors={themeColors} />
                <DetailRow label="الرسالة" value={selected.message_ar || selected.message_en || '—'} colors={themeColors} />
                <DetailRow label="الكيان" value={`${selected.entity_type}${selected.entity_id ? ` #${selected.entity_id}` : ''} ${selected.entity_repr ? '— ' + selected.entity_repr : ''}`} colors={themeColors} />
                <DetailRow label="HTTP" value={`${selected.request_method || '—'} ${selected.request_path || ''}`} colors={themeColors} />
                <DetailRow label="الحالة" value={String(selected.status_code ?? '—')} colors={themeColors} />
                <DetailRow label="زمن الاستجابة" value={selected.response_ms != null ? `${selected.response_ms} ms` : '—'} colors={themeColors} />
                <DetailRow label="عنوان IP" value={selected.ip_address || '—'} colors={themeColors} />
                <DetailRow label="المتصفح" value={selected.user_agent || '—'} colors={themeColors} />
                <DetailRow label="النظام المصدر" value={selected.source_system || '—'} colors={themeColors} />
                <DetailRow label="معرف الجلسة" value={selected.session_id || '—'} colors={themeColors} />
                <DetailRow label="معرف الترابط" value={selected.correlation_id || '—'} colors={themeColors} />

                {selected.changes && Object.keys(selected.changes).length > 0 && (
                  <View style={{ marginTop: 12 }}>
                    <Text style={[styles.detailLabel, { color: themeColors.textMuted }]}>التغييرات</Text>
                    <View style={[styles.codeBlock, { backgroundColor: isDark ? '#1F2937' : '#F9FAFB' }]}>
                      <Text style={{
                        color: themeColors.textPrimary,
                        fontFamily: 'monospace' as any,
                        fontSize: 12,
                        textAlign: 'left',
                      }}>
                        {JSON.stringify(selected.changes, null, 2)}
                      </Text>
                    </View>
                  </View>
                )}

                {selected.extra && Object.keys(selected.extra).length > 0 && (
                  <View style={{ marginTop: 12 }}>
                    <Text style={[styles.detailLabel, { color: themeColors.textMuted }]}>بيانات إضافية</Text>
                    <View style={[styles.codeBlock, { backgroundColor: isDark ? '#1F2937' : '#F9FAFB' }]}>
                      <Text style={{
                        color: themeColors.textPrimary,
                        fontFamily: 'monospace' as any,
                        fontSize: 12,
                        textAlign: 'left',
                      }}>
                        {JSON.stringify(selected.extra, null, 2)}
                      </Text>
                    </View>
                  </View>
                )}

                <TouchableOpacity
                  style={[styles.modalCloseBtn, { borderColor: themeColors.border }]}
                  onPress={() => setSelected(null)}
                >
                  <Text style={[styles.modalCloseBtnText, { color: themeColors.textPrimary }]}>إغلاق</Text>
                </TouchableOpacity>
              </ScrollView>
            </View>
          </View>
        )}
      </Modal>
    </PageContainer>
  );
}

interface DetailRowProps {
  label: string;
  value: string;
  colors: { textPrimary: string; textMuted: string };
}
function DetailRow({ label, value, colors }: DetailRowProps) {
  return (
    <View style={styles.detailRow}>
      <Text style={[styles.detailLabel, { color: colors.textMuted }]}>{label}</Text>
      <Text style={[styles.detailValue, { color: colors.textPrimary }]} selectable>{value}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  headerCard: { padding: 16, borderRadius: 12, borderWidth: 1 },
  title: { fontSize: 20, fontFamily: FONTS.medium, textAlign: 'right' },
  subtitle: { fontSize: 13, fontFamily: FONTS.regular, textAlign: 'right', marginTop: 4 },

  filterCard: { padding: 16, borderRadius: 12, borderWidth: 1, gap: 12 },
  filterTitle: { fontSize: 14, fontFamily: FONTS.medium, textAlign: 'right' },
  filterRow: { flexDirection: 'row-reverse', gap: 12, flexWrap: 'wrap' },
  filterField: { flex: 1, minWidth: 200 },
  filterLabel: { fontSize: 12, fontFamily: FONTS.regular, textAlign: 'right', marginBottom: 4 },
  input: {
    borderWidth: 1, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10,
    fontSize: 13, fontFamily: FONTS.regular, minHeight: 44,
  },
  clearBtn: {
    alignSelf: 'flex-start',
    paddingVertical: 6, paddingHorizontal: 14,
    backgroundColor: '#F3F4F6', borderRadius: 6,
  },
  clearBtnText: { color: '#374151', fontSize: 12, fontFamily: FONTS.medium },

  emptyCard: { padding: 40, borderRadius: 12, borderWidth: 1, alignItems: 'center' },
  emptyText: { fontSize: 14, fontFamily: FONTS.regular },

  tableCard: { borderRadius: 12, borderWidth: 1, overflow: 'hidden' },
  tableRow: {
    flexDirection: 'row-reverse', alignItems: 'center', gap: 12,
    paddingHorizontal: 14, paddingVertical: 10,
    borderBottomWidth: 1,
  },
  tableHeader: { backgroundColor: '#F9FAFB' },
  headerCellText: {
    fontSize: 12, fontFamily: FONTS.medium, color: '#6B7280',
    textAlign: 'right',
  },
  cellTime: { width: 160, fontSize: 12, fontFamily: FONTS.regular, textAlign: 'right' },
  cellUser: { width: 140, fontSize: 13, fontFamily: FONTS.regular, textAlign: 'right' },
  cellAction: { width: 120, alignItems: 'flex-end' },
  cellEntity: { width: 160, fontSize: 12, fontFamily: FONTS.regular, textAlign: 'right' },
  cellPath: { flex: 1, fontSize: 11, fontFamily: 'monospace' as any, textAlign: 'left' },
  cellStatus: { width: 60, fontSize: 12, fontFamily: FONTS.medium, textAlign: 'center' },
  actionBadge: {
    paddingVertical: 3, paddingHorizontal: 8, borderRadius: 6,
  },
  actionBadgeText: { fontSize: 11, fontFamily: FONTS.medium },

  pagination: {
    flexDirection: 'row-reverse', alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 14, paddingVertical: 10,
    borderTopWidth: 1,
  },
  paginationText: { fontSize: 12, fontFamily: FONTS.regular },
  pageBtn: {
    paddingVertical: 6, paddingHorizontal: 14,
    backgroundColor: COLORS.primary, borderRadius: 6,
  },
  pageBtnText: { color: '#FFFFFF', fontSize: 12, fontFamily: FONTS.medium },

  modalOverlay: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center', alignItems: 'center', padding: 16,
  },
  modalCard: {
    width: '100%', maxWidth: 720, maxHeight: '90%',
    padding: 20, borderRadius: 12, borderWidth: 1,
  },
  modalTitle: {
    fontSize: 18, fontFamily: FONTS.medium, textAlign: 'right', marginBottom: 16,
  },
  detailRow: {
    flexDirection: 'row-reverse', alignItems: 'flex-start', gap: 12,
    paddingVertical: 6,
  },
  detailLabel: {
    width: 140, fontSize: 12, fontFamily: FONTS.medium, textAlign: 'right',
  },
  detailValue: {
    flex: 1, fontSize: 13, fontFamily: FONTS.regular, textAlign: 'right',
  },
  codeBlock: {
    padding: 10, borderRadius: 6, marginTop: 4,
  },
  modalCloseBtn: {
    alignSelf: 'stretch', paddingVertical: 10, marginTop: 16,
    borderWidth: 1, borderRadius: 8, alignItems: 'center',
  },
  modalCloseBtnText: { fontSize: 14, fontFamily: FONTS.medium },
});
